import {Repository} from "../Repository";
import {AxiosInstance} from "axios";
import * as signalR from "@microsoft/signalr";
import {AppError} from "@common/AppError";
import Debug from "debug";
import {ChannelSubscriptionCounter} from "@api/repositories/reatime-repository/ChannelSubscriptionCounter";
import {ObserversManager} from "@api/repositories/reatime-repository/ObserversManager";
import {RealTimeMethodsInvoker} from "@api/repositories/reatime-repository/RealTimeMethodsInvoker";
import {Observers} from "@api/repositories/reatime-repository/types";
import {RealtimeConnection} from "@api/repositories/reatime-repository/RealtimeConnection";

const debug = Debug("realtime");

export class RealtimeRepository implements Repository {
    static DEFAULT_RETRY_CONNECTION_TIMEOUT_IN_MILLISECONDS = 5000;

    client: AxiosInstance;
    private connection: RealtimeConnection;
    private isConnecting = false;
    private retryConnectionTimeout = RealtimeRepository.DEFAULT_RETRY_CONNECTION_TIMEOUT_IN_MILLISECONDS;
    private attachQueue: Observers[] = [];
    private readonly subscriptionCounter: ChannelSubscriptionCounter;
    private isReset = false;
    private readonly observersManager: ObserversManager;
    private readonly invoker: RealTimeMethodsInvoker;
    private connectionQueue: ((value: unknown) => void)[] = [];

    constructor(client: AxiosInstance) {
        this.client = client;
        this.connection = new RealtimeConnection(this.onClose.bind(this), (client as any)._backend);
        this.invoker = new RealTimeMethodsInvoker(this.client, this.connection);
        this.subscriptionCounter = new ChannelSubscriptionCounter();
        this.observersManager = new ObserversManager(this.connection, this.subscriptionCounter);
    }

    private isConnected() {
        return this.connection.hub && this.connection.hub.state === 'Connected';
    }

    async waitForAnotherStart() {
        return new Promise(resolve => {
            const connected = this.isConnected();
            if (!connected) {
                this.connectionQueue.push(resolve);
            }
            else {
                resolve(1);
            }
        })
    }

    async start() {
        if (this.isConnecting) {
            // Ждём, пока не подключится другой вызов start()
            await this.waitForAnotherStart();
            return;
        }

        try {
            debug(`start connecting to ${process.env.VUE_APP_SERVER}${(this.client as any)._backend}/realtime through transport ${signalR.HttpTransportType[this.connection.usedTransport]}`);
            this.isConnecting = true;
            await this.connection.hub.start();

            debug('connected');
            await this.restoreObservers();
            await this.processQueue();
            this.processConnectionQueue();

            if (this.isReset) {
                this.observersManager?.notifyAll({ info: 'realtimeConnectionEstablished'});
                this.isReset = false;
            }
        } catch (err) {
            console.log('starting error', err);
            debug(`connection error, next retry in ${Math.floor(this.retryConnectionTimeout / 1000)} seconds`);
            this.retryStart();
        } finally {
            this.isConnecting = false;
        }
    }

    private retryStart() {
        setTimeout(() => {
            this.connection.reinitialize();
            this.start();
        }, this.retryConnectionTimeout);
    }

    private async onClose() {
        debug('connection was closed');
        if (this.connection.usedTransport === signalR.HttpTransportType.WebSockets || this.connection.usedTransport === signalR.HttpTransportType.ServerSentEvents) {
            this.observersManager?.notifyAll({ error: 'realtimeConnectionReset'});
            this.isReset = true;
        }
        await this.start();
    }

    async attach(observers: Observers) {
        try {
            if (!this.isConnected()) {
                await this.start()
            }

            const subscribeResult: any = await this.invoker?.subscribe(Object.keys(observers));

            await this.observersManager?.attach(observers);

            return subscribeResult;
        } catch (e) {
            this.attachQueue.push(observers);
            throw new AppError('SignalRHubError', e.message, e);
        }
    }

    // удаляет наблюдателя
    async detach(observers: Observers) {
        let index: number;
        index = this.attachQueue.indexOf(observers);
        if (index >= 0) {
            debug('founded in attach queue');
            this.attachQueue.splice(index, 1);
            return;
        }

        try {
            if (!this.isConnected()) {
                await this.start()
            }

            const channelsToUnsubscribe = await this.observersManager?.detach(observers);

            if (channelsToUnsubscribe && channelsToUnsubscribe.length) {
                await this.invoker?.unsubscribe(channelsToUnsubscribe);
                this.subscriptionCounter.removeSubscription(channelsToUnsubscribe);
            }
        } catch (e) {
            throw new AppError('SignalRHubError', e.message, e);
        }
    }

    async processQueue() {
        try {
            debug (`processing ${this.attachQueue.length} items in attach queue`);
            while (this.attachQueue.length > 0) {
                await this.attach(this.attachQueue[0]);
                this.attachQueue.shift();
            }
        } catch(e) {
            debug (`error while processing attach queue`);
            throw new AppError('SignalRHubError', e.message, e);
        }
    }

    async restoreObservers() {
        await this.observersManager?.restore();
        const channels = this.observersManager?.getChannels();
        if (channels && channels.length) {
            await this.invoker?.subscribe(channels);
        }
    }

    /**
     * resolving promises which waiting connection
     */
    private processConnectionQueue() {
        this.connectionQueue.forEach((resolve) => resolve(1));
        this.connectionQueue = [];
    }

    async clear() {
        const channels = this.observersManager?.getChannels();
        await this.observersManager.detachAll();
        if (!(this.client as any).checkIsWrongRefreshToken()) {
            await this.invoker?.unsubscribe(channels);
        }
        this.attachQueue = [];
        this.subscriptionCounter.clear();

        if (this.connectionQueue.length) {
            this.processConnectionQueue();
        }

        await this.connection.hub.stop();
    }
}
